/**
 * Post-Launch v3 · M2 (Fr 04.09.2026, 09:00) — /wirkung-gestalten radikal reduziert.
 *
 * Struktur (Steve-Jobs-Test):
 *   1. Header (Phase 3, was ist das hier)
 *   2. CTA-Hero: „Gestalte deinen nächsten Aufschlag" + Erklärung + Anliegen-Btn
 *   3. Star-Modell (6 Ebenen als Sterne-Grid mit Hover-Erklärung)
 *   4. Optional: Türöffner-Beispiele nach Klick auf Star-Ebene
 *
 * Raus (laut Andreas M2):
 *   - Acht-Etappen-Flexpfad-Sektion
 *   - Weg-von-Anliegen-bis-Aufschlag Heading
 *   - Eingangs-Frage mit 4 Options + Auto-Scroll
 *   - Zugangscode-Zwischenblock zwischen Frage und Türöffner
 */
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  MessageSquare, ArrowRight, MessageCircle, Volume2,
  Users, Target, HelpCircle,
} from "lucide-react";
import RedesignLayout from "@/components/redesign/RedesignLayout";
import { getTueroeffnerAntwort } from "@/lib/wirkungGestalten";

// Sprint V14 Fix 1 (Boardroom-CoS Sa 05.09.2026-19 Uhr) — Framework auf
// 3 Ebenen + Kontext reduziert. MIT WEM wird zur Kontext-Eyebrow-Box (der
// Fokustyp-Kontext, auf den die 3 Ebenen ausgerichtet sind). WOZU entfaellt.
// Neue Ebene 3 "Körpersprache" ersetzt die 4. Ebene.
const STAR_EBENEN = [
  {
    key: "was",
    label: "WAS",
    frage: "Was sagst du?",
    icon: MessageSquare,
    subline: "Inhalt",
    beschreibung: "Der Kern deiner Botschaft — die Sache, um die es geht. Fakten, Beispiele, konkrete Ergebnisse.",
  },
  {
    key: "wie",
    label: "WIE",
    frage: "Wie sagst du es?",
    icon: Volume2,
    subline: "Tonalität",
    beschreibung: "Der Klang deiner Worte — warm, sachlich, entschieden. Bilder oder Zahlen? Kurz oder ausführlich?",
  },
  {
    key: "koerpersprache",
    label: "KÖRPERSPRACHE",
    frage: "Wie zeigst du es nonverbal?",
    icon: Users,
    subline: "Nonverbal",
    beschreibung: "Blickkontakt, Haltung, Gestik, Nähe — was dein Körper sagt, während dein Mund spricht.",
  },
];

function CtaHero() {
  const navigate = useNavigate();
  return (
    <section
      className="rounded-2xl bg-white dark:bg-slate-900 border-2 border-[var(--fts-primary-bright)] p-6 sm:p-8"
      data-testid="wirkung-cta-hero"
    >
      <p className="text-xs font-bold uppercase tracking-widest text-[var(--fts-primary-bright)]">
        Anliegen-Tool
      </p>
      <h2 className="mt-1 text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900 dark:text-white">
        Gestalte deinen nächsten Aufschlag
      </h2>
      <p className="mt-2 text-sm sm:text-base text-slate-700 dark:text-slate-200 leading-relaxed max-w-xl">
        Du beschreibst kurz worauf es ankommt. Wir helfen dir die Situation so vorzubereiten, dass dein erster Satz wirklich ankommt.
      </p>
      <button
        type="button"
        onClick={() => navigate("/anliegen")}
        data-testid="wirkung-cta-anliegen-btn"
        className="mt-5 inline-flex items-center gap-2 rounded-xl bg-[var(--fts-primary-bright)] hover:opacity-90 text-white font-bold text-base px-6 py-3 shadow-md"
      >
        Neues Anliegen erstellen
        <ArrowRight className="w-4 h-4" />
      </button>
    </section>
  );
}

function StarModell({ activeKey, onSelect }) {
  return (
    <section
      className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-5 sm:p-6"
      data-testid="wirkung-star-modell"
    >
      <div className="mb-4">
        <p className="text-xs font-bold uppercase tracking-widest text-[var(--fts-primary-bright)]">
          Dein Kommunikations-Framework
        </p>
        <h3 className="mt-1 text-xl font-extrabold text-slate-900 dark:text-white">
          Drei Stellschrauben im Gespräch.
        </h3>
        <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
          Tippe eine Ebene an, um zu sehen, was sie bedeutet.
        </p>
      </div>

      {/* Sprint V14 Fix 1 — Kontext-Box "MIT WEM": kein eigenes Grid-Element mehr,
          sondern Kontext-Eyebrow oberhalb der drei Ebenen. */}
      <div
        data-testid="wirkung-kontext-mit-wem"
        className="mb-4 rounded-md border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/60 px-4 py-3"
      >
        <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-slate-500 dark:text-slate-400">
          Kontext · Mit wem
        </p>
        <p className="mt-1 text-sm text-slate-700 dark:text-slate-200">
          Welcher Fokustyp ist dein Gegenüber? Das ist der Türöffner-Kontext, auf den sich die drei Ebenen ausrichten.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3">
        {STAR_EBENEN.map((e) => {
          const Icon = e.icon;
          const isActive = activeKey === e.key;
          return (
            <button
              key={e.key}
              type="button"
              onClick={() => onSelect(e.key === activeKey ? null : e.key)}
              data-testid={`wirkung-star-${e.key}`}
              data-active={isActive ? "true" : "false"}
              aria-pressed={isActive}
              className={[
                "group text-left rounded-xl border p-3 sm:p-4 transition-all",
                isActive
                  ? "bg-[var(--fts-primary-bright)] border-[var(--fts-primary-bright)] text-white shadow-md"
                  : "bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 hover:border-[var(--fts-primary-bright)]/50",
              ].join(" ")}
            >
              <div className={["flex items-center gap-2 mb-1", isActive ? "text-white" : "text-[var(--fts-primary-bright)]"].join(" ")}>
                <Icon className="w-4 h-4 flex-shrink-0" />
                <p className="text-sm font-extrabold tracking-wider">{e.label}</p>
              </div>
              <p className={["text-[10px] font-semibold uppercase tracking-widest leading-snug", isActive ? "text-white/80" : "text-slate-400 dark:text-slate-500"].join(" ")}>
                {e.subline}
              </p>
              <p className={["mt-1 text-xs leading-snug", isActive ? "text-white/85" : "text-slate-500 dark:text-slate-400"].join(" ")}>
                {e.frage}
              </p>
            </button>
          );
        })}
      </div>

      {activeKey && (
        <div
          className="mt-4 rounded-xl bg-slate-50 dark:bg-slate-800/60 border-l-4 border-[var(--fts-primary-bright)] p-4"
          data-testid="wirkung-star-erklaerung"
        >
          <p className="text-sm text-slate-800 dark:text-slate-100 leading-snug">
            {STAR_EBENEN.find((e) => e.key === activeKey)?.beschreibung}
          </p>
        </div>
      )}

      {/* POSTLAUNCH-V6 · P1-#6 — Star-Modell wandert primär ins Anliegen-Tool */}
      <p
        className="mt-4 text-xs text-slate-500 dark:text-slate-400 italic"
        data-testid="wirkung-star-hinweis"
      >
        Konkrete Beispiele pro Fokustyp und Ebene siehst du im Anliegen-Tool, sobald du deine Situation beschreibst.
      </p>
    </section>
  );
}

function BrueckenFrage() {
  // M6 — emotionaler Anker
  return (
    <div
      className="rounded-xl bg-[var(--fts-primary-bright)]/10 border-l-4 border-[var(--fts-primary-bright)] px-4 py-3"
      data-testid="wirkung-bruecken-frage"
    >
      <div className="flex items-start gap-2">
        <HelpCircle className="w-4 h-4 text-[var(--fts-primary-bright)] mt-0.5 flex-shrink-0" aria-hidden="true" />
        <p className="text-sm font-semibold text-slate-800 dark:text-slate-100 leading-snug">
          Wen erkennst du wieder?
        </p>
      </div>
    </div>
  );
}

export default function PageWirkungGestalten() {
  const [activeEbene, setActiveEbene] = useState(null);

  return (
    <RedesignLayout>
      {({ data }) => {
        const user = data?.user || {};
        const rawSlug = user.primary_fokustyp || user.fokustyp_primary || null;
        const slug = rawSlug && ["eule", "hund", "katze", "biene"].includes(String(rawSlug).toLowerCase())
          ? String(rawSlug).toLowerCase()
          : "eule";

        return (
          <div
            className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6"
            data-testid="page-wirkung-gestalten"
            data-slug={slug}
          >
            <header>
              <p className="text-xs font-bold uppercase tracking-widest text-[var(--fts-primary-bright)]">
                Phase 3
              </p>
              <h1 className="mt-1 text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 dark:text-white">
                Kommunikation als Türöffner
              </h1>
            </header>

            <CtaHero />

            <StarModell activeKey={activeEbene} onSelect={setActiveEbene} />

            <BrueckenFrage />
          </div>
        );
      }}
    </RedesignLayout>
  );
}
